import React, { useState, useEffect } from "react";
import Logo from "../../assets/main_logo.jpg";
import {
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import { IoIosArrowDropright } from "react-icons/io";
import { MdSchedule, MdLocationOn, MdContactSupport } from "react-icons/md";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";
import { logoutUser } from "../../utils/helpers";
import Loading from "../../components/Loading/Loading";

const DispenserBranchVerify = () => {
  const { requestWithAuth } = useAuth();
  const { currentUser } = useGlobalContext();
  const [isLoading, setIsLoading] = useState(true);
  const [shiftData, setShiftData] = useState(null);
  const [hasShift, setHasShift] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showDenyConfirmation, setShowDenyConfirmation] = useState(false);

  useEffect(() => {
    fetchTodayShift();
  }, []);

  const fetchTodayShift = async () => {
    setIsLoading(true);
    try {
      const response = await requestWithAuth(`/users/shift/today/`, "GET");

      if (response?.success && response?.data?.id) {
        setShiftData(response?.data);
        setHasShift(true);
      } else {
        setHasShift(false);
      }
    } catch (error) {
      setHasShift(false);
      toast.error("Failed to fetch shift information. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmBranch = async () => {
    try {
      setIsConfirming(true);
      toast.success("Branch confirmed successfully! Redirecting to dashboard");
      const body = {
        "branch": shiftData?.branch,
        "session": shiftData?.session,
        "shift": shiftData?.id
      }
      const response = await requestWithAuth(`/users/session/`, "POST", body, {
        "Content-Type": "application/json"
      }).then(() => {
        localStorage.setItem("branch_confirmed", "true");
        if (currentUser?.branch == shiftData?.branch_name && currentUser?.role == "branch_manager") {
          window.location.href = "/branch-manager/dashboard"
        } else {
          window.location.href = "/pos/dispenser";
        }
      });

    } catch (error) {
      toast.error("Failed to confirm branch. Please try again.");
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDenyBranch = () => {
    setShowDenyConfirmation(true);
  };

  const confirmDeny = () => {
    setShowDenyConfirmation(false);
    logoutUser();
  };


  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <Loading title="Loading Schedule Details..." />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl mb-4 shadow-2xl">
              <img src={Logo} alt="LifeChemist" className="w-10 h-10" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">LifeChemist</h1>
            <p className="text-blue-200">Branch Verification</p>
          </div>

          {/* Main Content Card */}
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 relative">
            {/* Decorative elements */}
            <div className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full blur-2xl opacity-20"></div>
            <div className="absolute -bottom-2 -left-2 w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-xl opacity-20"></div>

            <div className="relative z-10">
              {!hasShift ? (
                // No Shift Available
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
                    <FaExclamationTriangle className="w-10 h-10 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    No Schedule Found
                  </h2>
                  <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                    You don't have a shift scheduled for today (
                    {formatDate(new Date().toISOString())}). Please contact your
                    administrator or branch manager to assign a shift for you.
                  </p>


                  <button
                    onClick={logoutUser}
                    className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-semibold flex items-center justify-center gap-2 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-200"
                  >
                    Logout
                    <IoIosArrowDropright className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                // Shift Available - Branch Confirmation
                <div>
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <MdSchedule className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Confirm Your Branch
                    </h2>
                    <p className="text-gray-600">
                      Please verify that you are at the correct branch location
                      for your shift.
                    </p>
                  </div>

                  {/* Shift Details */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <MdSchedule className="w-5 h-5 mr-2 text-green-600" />
                      Today's Shift Details
                    </h3>

                    <div className="grid md:grid-cols-3 gap-2">
                      {/* User Information */}
                      <div className="flex items-center mb-4">
                        <FaUser className="w-5 h-5 text-blue-600 mr-3" />
                        <div>
                          <p className="font-semibold text-gray-800">
                            Staff
                          </p>
                          <p className="text-sm text-gray-600 capitalize">
                            {currentUser?.first_name}{" "}
                            {currentUser?.last_name}
                          </p>
                        </div>

                      </div>
                      {/* Branch Information */}
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <MdLocationOn className="w-5 h-5 text-green-600 mr-3 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-gray-800">
                              Branch
                            </p>
                            <p className="text-sm text-gray-600 capitalize">
                              {shiftData?.branch_name}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Time Information */}
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <FaMapMarkerAlt className="w-5 h-5 text-purple-600 mr-3" />
                          <div>
                            <p className="font-semibold text-gray-800">
                              Session Type
                            </p>
                            <p className="text-sm text-gray-600 capitalize">
                              {shiftData?.session || "Regular Shift"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Confirmation Question */}
                  <div className="text-center mb-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      Are you currently at {shiftData?.branch_name}?
                    </h3>
                    <p className="text-gray-600">
                      Please confirm your location to proceed to the dispenser
                      dashboard.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    {isConfirming ? (
                      <div className="flex-1 py-4 px-6 rounded-xl bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold flex items-center justify-center gap-3">
                        <RotatingLines
                          visible={true}
                          height="20"
                          width="20"
                          color="transparent"
                          strokeColor="white"
                          strokeWidth="3"
                          animationDuration="0.75"
                          ariaLabel="rotating-lines-loading"
                        />
                        Confirming...
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={handleConfirmBranch}
                          className="flex-1 py-4 px-6 rounded-xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold flex items-center justify-center gap-2 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-200"
                        >
                          <FaCheckCircle className="w-5 h-5" />
                          Yes, I'm Here
                        </button>

                        <button
                          onClick={handleDenyBranch}
                          className="flex-1 py-4 px-6 rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold flex items-center justify-center gap-2 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-200"
                        >
                          <FaTimesCircle className="w-5 h-5" />
                          No, Wrong Location
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-xs text-blue-200">
              © 2025 LifeChemist. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Denial Confirmation Modal */}
      {showDenyConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <FaExclamationTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Schedule Modification Required
              </h3>
              <p className="text-gray-600 mb-6">
                Since you're not at the correct branch, please contact your
                branch manager or administrator to modify your schedule before
                logging in.
              </p>


              <div className="flex gap-3">
                <button
                  onClick={() => setShowDenyConfirmation(false)}
                  className="flex-1 py-3 px-4 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors duration-200"
                >
                  Go Back
                </button>
                <button
                  onClick={confirmDeny}
                  className="flex-1 py-3 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors duration-200"
                >
                  I Understand
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispenserBranchVerify;
